import { ADDRESS_SELECT_INPUT_TYPE, countryList, DATATYPES, DEFAULT_INPUT_SIZING, DELIVERY_DETAILS_ELEMENT_TYPE, DYNAMIC_DATA_SPLITTER, DYNAMIC_TYPE_INPUT_TYPE, EDITOR_INPUT, FILE_UPLOAD_INPUT, JSON_TABLE_INPUT_TYPE, RADIO_INPUT_TYPE, SELECT_INPUT_TYPE, VALIDATION_ERROR_MESSAGE } from "utils/Constants";
import JsonTableInput from "./components/JsonTableInput";
import DynamicTypeInput from "./components/DynamicTypeInput";
import DefaultInput from "./components/DefaultInput";
import SelectInput from "./components/SelectInput";
import AddressSelectInput from "./components/AddressSelectInput";
import DeliveryDetails from "./components/DeliveryDetails";
import { createElement } from "react";
import { FormFeedback } from "reactstrap";
import * as YupValidation from "yup";
import PhonetInput from "./components/PhonetInput";
import EditorInput from "./components/EditorInput";
import FileUploadInput from "./components/FileUploadInput";
import RadioInput from "./components/RadioInput";

YupValidation.setLocale({
    mixed: {
        required: 'REQUIRED_FIELD_ERROR',
    },
    string: {
        email: 'EMAIL_FIELD_ERROR',
    },
    number: {
        positive: 'POSITIVE_NUMBER_ERROR',
    },
});

export const Yup = YupValidation;

const INPUTS = {
    "input": DefaultInput,
    [JSON_TABLE_INPUT_TYPE]: JsonTableInput,
    [DYNAMIC_TYPE_INPUT_TYPE]: DynamicTypeInput,
    [SELECT_INPUT_TYPE]: SelectInput,
    [ADDRESS_SELECT_INPUT_TYPE]: AddressSelectInput,
    [DELIVERY_DETAILS_ELEMENT_TYPE]: DeliveryDetails,
    ["phone"]: PhonetInput,
    [EDITOR_INPUT]: EditorInput,
    [FILE_UPLOAD_INPUT]: FileUploadInput,
    [RADIO_INPUT_TYPE]: RadioInput,
};

/**
 * Make the form inputs uneditable, if the formInputs param is empty all the form become uneditable,
 * if a list of inputs is send only those inputs are marked as uneditable
 * 
 * @param {*} formConfig 
 * @param {*} formInputs 
 * @returns 
 */
export const makeFormInputsUneditable = (formConfig, formInputs = []) => {
    const newSections = formConfig.sections.map(s => {
        const section = { ...s };
        section.fields = section.fields.map(fr => {
            const fieldRow = fr.map(f => {
                let dataSchema = f.dataSchema;
                if (formInputs.length === 0 || formInputs.includes(f.name)) {
                    if (dataSchema) {
                        const newDataSchema = Object.keys(dataSchema).reduce((a, k) => {
                            const newData = { ...(dataSchema[k]) }
                            newData.disabled = true;
                            return {
                                ...a,
                                [k]: newData
                            }
                        }, {});
                        dataSchema = newDataSchema;
                    }
                    return {
                        ...f,
                        disabled: true,
                        dataSchema
                    }
                } else {
                    return {
                        ...f
                    }
                }
            });
            return fieldRow;
        });
        return section;
    })
    return {
        ...formConfig,
        sections: newSections
    }
}

export const makeFormUneditable = (formConfig) => {
    return makeFormInputsUneditable(formConfig);
}

export const generateInputTag = (id, inputData, formData, onChange, formik = {}, isEditMode = false) => {
    const {
        type: inputType, name, placeholder, valid, invalid, addon, min, max, step, onTableInput, rows,
        className, value, creatable = false, options, checkedValue, unCheckedValue, suffix, defaultValue, inputConfig } = inputData;
    const inputProps = {
        name, placeholder, valid, invalid, addon, suffix, onTableInput, defaultValue,
        min, max, step, value, className, checkedValue, unCheckedValue, rows, inputConfig
    };
    const type = getInputType(inputData);
    const isDisabled = isInputDisabled(inputData, formData, isEditMode);
    const dataType = getInputDataType(inputData);
    const selectOptions = getSelectOptions(options, inputType);

    const inputErrors = getInputErrors(inputData, formData, formik);

    if (inputErrors) {
        inputProps.invalid = true;
        inputData.inputErrors = inputErrors;
    } else {
        inputProps.invalid = false;
        inputData.inputErrors = null;
    }

    const inputTag = INPUTS[type] ? INPUTS[type] : INPUTS.input;
    return createElement(
        inputTag,
        {
            ...inputData,
            type,
            inputType,
            id,
            options: selectOptions,
            inputProps,
            creatable,
            isDisabled,
            formik,
            value: getFormValue(name, dataType, selectOptions, inputData, formData, inputProps),
            onBlur: formik.handleBlur,
            onChange: (e, inputName) => {
                const eventInputName = inputName || name;
                const value = getInputValue(e, dataType);
                if (formik.handleChange) {
                    if (e.nativeEvent) {
                        formik.handleChange(e);
                    } else {
                        if (value instanceof Object) {
                            for (const property in value) {
                                if (DATATYPES.FILE_INPUT === dataType) {
                                    formik.setFieldValue(property, value[property]);
                                } else {
                                    formik.setFieldValue(property, value);
                                }
                            }
                        } else {
                            formik.handleChange(value);
                        }
                    }
                }
                let eventData = value
                if (DATATYPES.FILE_INPUT === dataType) {
                    eventData = value[eventInputName]
                }
                onChange(eventInputName, eventData, dataType, e)
            }
        }
    );

}

export const getInputErrors = (inputData, formData, formik) => {
    const { errors = {}, touched = {}, submitCount } = formik;
    const { name } = inputData;
    const errorType = errors[name] || false;

    if (errorType && (touched[name] || submitCount > 0)) {
        return (
            <FormFeedback>
                {VALIDATION_ERROR_MESSAGE[errorType]}
            </FormFeedback>
        );
    }
    return null;

}

export const getInputSizing = (inputSizing) => {
    return {
        ...DEFAULT_INPUT_SIZING,
        ...inputSizing
    }
}

export const getFormValue = (field, dataType, selectOptions, inputData, formData, inputProps) => {
    let currentValue = formData[field] === undefined ? "" : formData[field];

    if (inputData.type === DYNAMIC_TYPE_INPUT_TYPE) {
        const { defaultOption } = inputData;
        if (!currentValue && defaultOption) {
            currentValue = `${DYNAMIC_DATA_SPLITTER}${defaultOption}`;
        }
    }

    if (inputData.type === ADDRESS_SELECT_INPUT_TYPE) {
        const { name } = inputData;
        currentValue = {
            [`${name}_province`]: formData[`${name}_province`] || "",
            [`${name}_canton`]: formData[`${name}_canton`] || "",
            [`${name}_district`]: formData[`${name}_district`] || ""
        };
    }
    if (inputData.type === "phone") {
        const { name } = inputData;
        currentValue = {
            [`${name}_cd`]: formData[`${name}_cd`] || "",
            [name]: formData[name] || ""
        };
    }

    if (DATATYPES.BOOLEAN === dataType) {
        return inputProps.checkedValue ? inputProps.checkedValue === currentValue : currentValue
    }


    if (DATATYPES.SELECT === dataType) {
        const { defaultValue } = inputData;
        if (!currentValue && defaultValue) {
            currentValue = defaultValue;
        }

        const option = selectOptions?.filter(({ value }) => value === currentValue) || [];
        return option.length === 0 ?
            (currentValue ? createSelectOption(currentValue) : "")
            : option[0];
    } else {
        return currentValue;
    }
}

const createSelectOption = (label) => ({
    label,
    value: label,
});

const getInputValue = (e, datatype) => {
    let value = "";

    if (DATATYPES.INT === datatype) {
        value = e.target.value
        value = value === "" ? "0" : value;
        value = parseInt(value);
    } else if (DATATYPES.SELECT === datatype) {
        value = e ? e.value : "";
    } else if (DATATYPES.DECIMAL === datatype) {
        value = e.target.value
        if (e.nativeEvent.data === ".") {
            return;
        }
        value = value === "" ? "0" : value;
        value = Number(value);
    } else if (DATATYPES.BOOLEAN === datatype) {
        value = e.target;
        value = value.checked || false;

        if (e.checkedValue != null && e.unCheckedValue != null) {
            value = value ? e.checkedValue : e.unCheckedValue;
        }
    } else if (DATATYPES.STRING === datatype) {
        value = e.target.value;
    } else if (DATATYPES.RAW === datatype || DATATYPES.ADDRESS === datatype || "phone" === datatype || DATATYPES.FILE_INPUT === datatype) {
        value = e;
    }

    return value;
}

export const getSelectOptions = (options, inputType) => {
    let allOptions;
    if (inputType === "country") {
        allOptions = { ...countryList };
    } else if (options) {
        allOptions = { ...options };
    }

    const selectOptions = allOptions && Object.keys(allOptions).map((opt) => {
        const valueLabel = allOptions[opt];
        if (typeof valueLabel === "object") {
            return allOptions[opt];
        }
        return { value: opt, label: valueLabel };
    });
    return selectOptions
}
export const isInputDisabled = (inputData, formData, isEditMode) => {
    const { disabled, disabledOnEdit, disabledOnCreate, disableIf } = inputData
    let isInputDisabled = disabled || (isEditMode && disabledOnEdit) || (!isEditMode && disabledOnCreate);
    if (disableIf) {
        isInputDisabled = isInputDisabled || Object.keys(disableIf).reduce(
            (acc, key) => {
                const comparison = disableIf[key];
                const compareFormValue = formData[key];
                return acc || comparison.reduce(
                    (c, compareActions) => {
                        const compareActionsResult = Object.entries(compareActions).reduce(
                            (cr, compareAction) => {
                                let comparisonResult = false
                                if (compareAction[0] === "eq") {
                                    comparisonResult = compareAction[1] === compareFormValue
                                }
                                return cr || comparisonResult;
                            }, false);
                        return c || compareActionsResult;
                    }, false
                );
            },
            false);
    }
    return isInputDisabled;
}

export const getInputDataType = (inputData) => {
    const { type, isDecimal = false, valueAsString = false } = inputData;
    const defaultDataType = DATATYPES.STRING;
    const typeDataType = {
        "number": DATATYPES.INT,
        "decimal": DATATYPES.DECIMAL,
        "checkbox": DATATYPES.BOOLEAN,
        "select": DATATYPES.SELECT,
        "raw": DATATYPES.RAW,
        "address": DATATYPES.ADDRESS,
        "fileInput": DATATYPES.FILE_INPUT
    };
    let inputType = type;
    if (valueAsString) {
        inputType = "string";
    } else if (isDecimal) {
        inputType = "decimal";
    } else if (inputType === JSON_TABLE_INPUT_TYPE || inputType === DYNAMIC_TYPE_INPUT_TYPE) {
        inputType = "raw";
    } else if (inputType === "country") {
        inputType = SELECT_INPUT_TYPE;
    } else if (inputType === ADDRESS_SELECT_INPUT_TYPE) {
        inputType = "address";
    } else if (inputType === "phone") {
        inputType = "phone";
    } else if (inputType === EDITOR_INPUT) {
        inputType = "raw";
    } else if (inputType === FILE_UPLOAD_INPUT) {
        inputType = "fileInput";
    }

    return typeDataType[inputType] || defaultDataType;
}

export const getInputType = (inputData) => {
    const { type } = inputData;
    let inputType = type;
    if (type === "country") {
        inputType = SELECT_INPUT_TYPE;
    } else if (type === "money") {
        inputType = "number";
    }
    return inputType;
}

export const updateInputProperties = (formConfig, inputName, properties) => {
    for (let section of formConfig.sections) {
        for (let group of section.fields) {
            group.forEach((input, index, groupArray) => {
                if (input.name === inputName) {
                    const newProperties = {
                        ...input,
                        ...properties
                    };
                    groupArray[index] = newProperties
                }
            })
        }
    }
    return formConfig;
}